import { sha256 } from '@noble/hashes/sha2.js'
import { bytesToHex } from '@noble/hashes/utils.js'
import axios from 'axios'

import api from '~api/api'
import { type AssetType, type CaseId, type IdentityId } from '~types'

import { type IUploadAssetResponse } from './upload_utils'

export const MULTIPART_THRESHOLD_BYTES = 20 * 1024 * 1024
export const MAX_CONCURRENT_UPLOADS = 3

export interface IUploadSlots {
  acquire: () => Promise<void>
  release: () => void
}

export const createUploadSlots = (maxConcurrent = MAX_CONCURRENT_UPLOADS): IUploadSlots => {
  let active = 0
  const waiting: (() => void)[] = []
  return {
    acquire: async (): Promise<void> => {
      if (active >= maxConcurrent) {
        await new Promise<void>((resolve) => waiting.push(resolve))
        return
      }
      active += 1
    },
    release: (): void => {
      const next = waiting.shift()
      if (next) next()
      else active -= 1
    },
  }
}

const HASH_CHUNK_BYTES = 64 * 1024 * 1024
const PART_MAX_ATTEMPTS = 4
const PART_CONCURRENCY = 4
const PART_RETRY_BASE_DELAY_MS = 500
const PART_URL_REFRESH_AFTER_MS = 10 * 60 * 1000
const ACCESSIBLE_URL_REFRESH_AFTER_MS = 30 * 60 * 1000
const HTTP_FORBIDDEN = 403

interface ICreatePresignedUploadResponse {
  key: string
  url: string
  is_secure: boolean
  accessible_url: string
  expires_in: number
  upload_url: string | null
  upload_headers: Record<string, string>
  upload_id: string | null
  part_size: number | null
  part_urls: string[] | null
}

interface IMultipartPart {
  part_number: number
  etag: string
  size?: number | null
}

interface IResumeMultipartUploadResponse {
  expires_in: number
  part_urls: string[]
  uploaded_parts: IMultipartPart[]
}

export interface IPresignedUploadOptions {
  caseHandle?: string | null
  caseId?: CaseId | null
  isSecure?: boolean
  organizationIdentityId?: IdentityId
  multipart?: boolean
  onProgress?: (fraction: number) => void
  signal?: AbortSignal
}

export interface IMultipartUploadState {
  organizationIdentityId: IdentityId
  key: string
  uploadId: string
  isSecure: boolean
  partSize: number
  partCount: number
}

export class MultipartUploadError extends Error {
  state: IMultipartUploadState

  constructor(state: IMultipartUploadState, cause: unknown) {
    super(cause instanceof Error ? cause.message : 'Multipart upload failed', { cause })
    this.name = 'MultipartUploadError'
    this.state = state
  }
}

const throwIfAborted = (signal: AbortSignal | undefined): void => {
  if (signal?.aborted) {
    throw new axios.CanceledError()
  }
}

// Hashed one slice at a time: browsers cap a single Blob read at around 2GB, so reading the
// whole file into one ArrayBuffer fails for large archives.
export const sha256Hex = async (file: Blob, signal?: AbortSignal): Promise<string> => {
  const hash = sha256.create()
  for (let offset = 0; offset < file.size; offset += HASH_CHUNK_BYTES) {
    throwIfAborted(signal)
    const chunk = file.slice(offset, Math.min(offset + HASH_CHUNK_BYTES, file.size))
    hash.update(new Uint8Array(await chunk.arrayBuffer()))
  }
  throwIfAborted(signal)
  return bytesToHex(hash.digest())
}

const sleep = (milliseconds: number, signal: AbortSignal | undefined): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new axios.CanceledError())
      return
    }
    const onAbort = (): void => {
      clearTimeout(timer)
      reject(new axios.CanceledError())
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, milliseconds)
    signal?.addEventListener('abort', onAbort, { once: true })
  })

const isExpiredUrlError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === HTTP_FORBIDDEN

const putWithRetry = async (
  getUrl: (forceRefresh: boolean) => Promise<string>,
  body: Blob,
  headers: Record<string, string>,
  signal: AbortSignal | undefined,
  onProgress: (loaded: number) => void,
): Promise<string> => {
  let forceRefresh = false
  for (let attempt = 1; ; attempt++) {
    try {
      const response = await axios.put(await getUrl(forceRefresh), body, {
        headers,
        signal,
        withCredentials: false,
        onUploadProgress: (event) => onProgress(event.loaded),
      })
      const etag = response.headers['etag'] as string | undefined
      return etag?.replace(/"/g, '') ?? ''
    } catch (error) {
      if (axios.isCancel(error) || attempt >= PART_MAX_ATTEMPTS) {
        throw error
      }
      forceRefresh = isExpiredUrlError(error)
      if (!forceRefresh) {
        await sleep(PART_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1), signal)
      }
    }
  }
}

const fetchResumeState = async (
  state: IMultipartUploadState,
): Promise<IResumeMultipartUploadResponse> => {
  const response = await api.post<IResumeMultipartUploadResponse>(
    '/core/resume-multipart-upload/',
    {
      organization_identity_id: state.organizationIdentityId,
      key: state.key,
      upload_id: state.uploadId,
      is_secure: state.isSecure,
      part_count: state.partCount,
    },
  )
  return response.data
}

const uploadParts = async (
  file: File,
  state: IMultipartUploadState,
  initialPartUrls: string[],
  alreadyUploaded: IMultipartPart[],
  onProgress: ((fraction: number) => void) | undefined,
  signal: AbortSignal | undefined,
): Promise<IMultipartPart[]> => {
  const parts = new Map<number, IMultipartPart>(
    alreadyUploaded.map((part) => [part.part_number, part]),
  )
  const loadedByPart = new Map<number, number>()
  for (const part of alreadyUploaded) {
    loadedByPart.set(part.part_number, part.size ?? state.partSize)
  }
  const report = (): void => {
    if (!onProgress) return
    const loaded = Array.from(loadedByPart.values()).reduce((sum, bytes) => sum + bytes, 0)
    onProgress(Math.min(1, loaded / file.size))
  }

  let partUrls = initialPartUrls
  let urlsIssuedAt = Date.now()
  let refreshing: Promise<void> | null = null
  const refreshUrls = (): Promise<void> => {
    refreshing ??= fetchResumeState(state)
      .then((resumed) => {
        partUrls = resumed.part_urls
        urlsIssuedAt = Date.now()
      })
      .finally(() => {
        refreshing = null
      })
    return refreshing
  }
  const getPartUrl = async (partNumber: number, forceRefresh: boolean): Promise<string> => {
    if (forceRefresh || Date.now() - urlsIssuedAt > PART_URL_REFRESH_AFTER_MS) {
      await refreshUrls()
    }
    return partUrls[partNumber - 1]
  }

  const pending = Array.from({ length: state.partCount }, (_, index) => index + 1).filter(
    (partNumber) => !parts.has(partNumber),
  )
  const workersController = new AbortController()
  const stopWorkers = (): void => workersController.abort()
  if (signal?.aborted) {
    stopWorkers()
  } else {
    signal?.addEventListener('abort', stopWorkers, { once: true })
  }
  let firstError: unknown = null
  const worker = async (): Promise<void> => {
    for (;;) {
      const partNumber = pending.shift()
      if (partNumber === undefined || workersController.signal.aborted) return
      const start = (partNumber - 1) * state.partSize
      const chunk = file.slice(start, Math.min(start + state.partSize, file.size))
      try {
        const etag = await putWithRetry(
          (forceRefresh) => getPartUrl(partNumber, forceRefresh),
          chunk,
          {},
          workersController.signal,
          (loaded) => {
            loadedByPart.set(partNumber, loaded)
            report()
          },
        )
        parts.set(partNumber, { part_number: partNumber, etag, size: chunk.size })
      } catch (error) {
        firstError ??= error
        stopWorkers()
        return
      }
    }
  }
  try {
    await Promise.all(Array.from({ length: PART_CONCURRENCY }, worker))
  } finally {
    signal?.removeEventListener('abort', stopWorkers)
  }
  if (firstError !== null) {
    throw firstError
  }
  if (signal?.aborted) {
    throw new axios.CanceledError()
  }
  return Array.from(parts.values()).sort((left, right) => left.part_number - right.part_number)
}

const completeMultipartUpload = async (
  state: IMultipartUploadState,
  parts: IMultipartPart[],
): Promise<IUploadAssetResponse> => {
  const response = await api.post<IUploadAssetResponse>('/core/complete-multipart-upload/', {
    organization_identity_id: state.organizationIdentityId,
    key: state.key,
    upload_id: state.uploadId,
    is_secure: state.isSecure,
    parts,
  })
  return response.data
}

const runMultipartUpload = async (
  file: File,
  state: IMultipartUploadState,
  partUrls: string[],
  alreadyUploaded: IMultipartPart[],
  onProgress: ((fraction: number) => void) | undefined,
  signal: AbortSignal | undefined,
): Promise<IUploadAssetResponse> => {
  try {
    const parts = await uploadParts(file, state, partUrls, alreadyUploaded, onProgress, signal)
    return await completeMultipartUpload(state, parts)
  } catch (error) {
    if (axios.isCancel(error)) {
      await abortMultipartUpload(state).catch(() => undefined)
      throw error
    }
    throw new MultipartUploadError(state, error)
  }
}

export const abortMultipartUpload = async (state: IMultipartUploadState): Promise<void> => {
  await api.post('/core/abort-multipart-upload/', {
    organization_identity_id: state.organizationIdentityId,
    key: state.key,
    upload_id: state.uploadId,
    is_secure: state.isSecure,
  })
}

export const resumeMultipartUpload = async (
  file: File,
  state: IMultipartUploadState,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<IUploadAssetResponse> => {
  const resumed = await fetchResumeState(state)
  return runMultipartUpload(
    file,
    state,
    resumed.part_urls,
    resumed.uploaded_parts,
    onProgress,
    signal,
  )
}

export const uploadFilePresigned = async (
  ownerId: IdentityId,
  file: File,
  assetType: AssetType,
  options: IPresignedUploadOptions = {},
): Promise<IUploadAssetResponse> => {
  const {
    caseHandle = null,
    caseId = null,
    isSecure = false,
    organizationIdentityId = ownerId,
    multipart = file.size >= MULTIPART_THRESHOLD_BYTES,
    onProgress,
    signal,
  } = options

  const contentSha256 = await sha256Hex(file, signal)
  const createPresignedUpload = async (): Promise<ICreatePresignedUploadResponse> => {
    const response = await api.post<ICreatePresignedUploadResponse>(
      '/core/create-presigned-upload/',
      {
        organization_identity_id: organizationIdentityId,
        owner_id: ownerId,
        file_name: file.name,
        file_size: file.size,
        content_sha256: contentSha256,
        content_type: file.type || null,
        is_secure: isSecure,
        asset_type: assetType,
        case_handle: caseHandle,
        case_id: caseId,
        multipart,
      },
    )
    return response.data
  }
  let presigned = await createPresignedUpload()
  let issuedAt = Date.now()

  if (presigned.upload_url) {
    await putWithRetry(
      async (forceRefresh) => {
        if (forceRefresh || Date.now() - issuedAt > PART_URL_REFRESH_AFTER_MS) {
          presigned = await createPresignedUpload()
          issuedAt = Date.now()
        }
        return presigned.upload_url ?? ''
      },
      file,
      presigned.upload_headers,
      signal,
      (loaded) => onProgress?.(Math.min(1, loaded / file.size)),
    )
    if (presigned.is_secure && Date.now() - issuedAt > ACCESSIBLE_URL_REFRESH_AFTER_MS) {
      presigned = await createPresignedUpload()
    }
    return {
      key: presigned.key,
      url: presigned.url,
      is_secure: presigned.is_secure,
      accessible_url: presigned.accessible_url,
    }
  }

  if (!presigned.upload_id || !presigned.part_urls || !presigned.part_size) {
    throw new Error('Presigned upload response is missing multipart details')
  }
  const state: IMultipartUploadState = {
    organizationIdentityId,
    key: presigned.key,
    uploadId: presigned.upload_id,
    isSecure: presigned.is_secure,
    partSize: presigned.part_size,
    partCount: presigned.part_urls.length,
  }
  return runMultipartUpload(file, state, presigned.part_urls, [], onProgress, signal)
}
